/**
 * © 2025 Malvys Exaucé Dessou — ZonePro. Tous droits réservés.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const LANGUAGES = {
  FR: 'fr',
  EN: 'en'
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('zonepro_language');
    return saved || LANGUAGES.FR;
  });

  useEffect(() => {
    localStorage.setItem('zonepro_language', language);
    document.documentElement.lang = language;
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === LANGUAGES.FR ? LANGUAGES.EN : LANGUAGES.FR);
  };

  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) {
        // Fallback to French if key not found
        value = translations[LANGUAGES.FR];
        for (const fk of keys) {
          value = value?.[fk];
        }
        break;
      }
    }
    
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      return value.replace(/\{(\w+)\}/g, (_, key) => params[key] ?? `{${key}}`);
    }
    
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Translations
const translations = {
  fr: {
    // Common
    common: {
      loading: 'Chargement...',
      error: 'Erreur',
      success: 'Succès',
      cancel: 'Annuler',
      confirm: 'Confirmer',
      save: 'Enregistrer',
      delete: 'Supprimer',
      edit: 'Modifier',
      back: 'Retour',
      next: 'Suivant',
      search: 'Rechercher',
      close: 'Fermer',
      yes: 'Oui',
      no: 'Non',
      or: 'ou',
      and: 'et',
      required: 'Requis',
      optional: 'Optionnel'
    },
    
    // Navigation
    nav: {
      dashboard: 'Tableau de bord',
      newProject: 'Nouveau Projet',
      projects: 'Projets',
      admin: 'Administration',
      logout: 'Déconnexion',
      login: 'Connexion',
      register: "S'inscrire"
    },
    
    // Auth
    auth: {
      login: 'Connexion',
      register: 'Inscription',
      email: 'Courriel',
      password: 'Mot de passe',
      fullName: 'Nom complet',
      forgotPassword: 'Mot de passe oublié?',
      noAccount: "Vous n'avez pas de compte?",
      hasAccount: 'Vous avez déjà un compte?',
      loginButton: 'Se connecter',
      registerButton: "S'inscrire",
      loginSuccess: 'Connexion réussie',
      registerSuccess: 'Compte créé avec succès',
      loginError: 'Erreur de connexion',
      registerError: "Erreur lors de l'inscription",
      welcomeBack: 'Bienvenue',
      createAccount: 'Créer un compte',
      startAnalyzing: 'Commencez à analyser le potentiel de développement'
    },
    
    // Dashboard
    dashboard: {
      title: 'Tableau de bord',
      welcome: 'Bienvenue sur ZonePro',
      recentProjects: 'Projets récents',
      noProjects: 'Aucun projet',
      createFirst: 'Créez votre premier projet pour commencer',
      stats: {
        totalProjects: 'Total Projets',
        analyzed: 'Analysés',
        draft: 'Brouillons',
        avgScore: 'Score Moyen'
      }
    },
    
    // New Project
    newProject: {
      title: 'Nouveau Projet',
      subtitle: 'Analysez le potentiel de développement de votre terrain',
      lotNumber: 'Numéro de lot cadastral',
      lotPlaceholder: 'Ex: 1 588 300',
      lotHelp: 'Trouvez le numéro sur votre acte notarié ou sur infolot.gouv.qc.ca',
      address: 'Adresse civique',
      addressPlaceholder: 'Entrez une adresse à Montréal',
      searchLot: 'Analyser ce lot',
      searchAddress: 'Rechercher cette adresse',
      searching: 'Recherche en cours...',
      analysisLaunched: 'Analyse lancée pour le lot {lot}',
      lotNotFound: 'Lot non trouvé',
      enterLot: 'Veuillez entrer un numéro de lot',
      enterAddress: 'Veuillez entrer une adresse',
      orUseAddress: 'Ou recherchez par adresse',
      primaryMethod: 'Méthode principale',
      alternativeMethod: 'Méthode alternative',
      projectType: 'Type de projet',
      residential: 'Résidentiel',
      commercial: 'Commercial',
      mixed: 'Mixte',
      dataStatus: {
        confirmed: 'Confirmé',
        computed: 'Calculé',
        estimated: 'Estimé',
        unavailable: 'Non disponible',
        planning: 'Planification',
        standard: 'Standard',
        specialSector: 'Secteur spécial',
        requiresValidation: 'Validation requise'
      }
    },
    
    // Project Detail
    project: {
      status: {
        analyzed: 'Analysé',
        draft: 'Brouillon'
      },
      tabs: {
        overview: 'Résumé',
        regulatory: 'Réglementaire',
        financial: 'Financier',
        scenarios: 'Scénarios',
        neighborhood: 'Quartier',
        sustainability: 'Durabilité'
      },
      score: {
        title: "Score d'opportunité",
        regulatory: 'Réglementaire',
        financial: 'Financier',
        neighborhood: 'Quartier',
        risk: 'Risque',
        tooltipTotal: "Évalue le potentiel de développement du terrain sur 100 points.",
        tooltipRegulatory: "Mesure la conformité aux règles de zonage.",
        tooltipFinancial: "Évalue la rentabilité potentielle basée sur les coûts.",
        tooltipNeighborhood: "Analyse le contexte urbain et la compatibilité.",
        tooltipRisk: "Évalue les risques potentiels et complexités."
      },
      overview: {
        recommendedScenario: 'Scénario recommandé',
        buildableArea: 'Surface constructible',
        estimatedStoreys: 'Étages estimés',
        estimatedUnits: 'Unités estimées',
        riskFactors: 'Facteurs de risque',
        validationRequired: 'Points à valider',
        noRisks: 'Aucun risque majeur identifié',
        noValidation: 'Aucune validation particulière requise'
      },
      regulatory: {
        zoneCode: 'Code de zone',
        permittedUses: 'Usages permis',
        maxHeight: 'Hauteur maximale',
        maxStoreys: 'Étages maximum',
        lotCoverage: "Taux d'implantation",
        setbacks: 'Marges de recul',
        front: 'Avant',
        rear: 'Arrière',
        sides: 'Latérales',
        existingBuilding: 'Bâtiment existant détecté',
        buildingArea: 'Superficie bâtiment',
        floors: 'Étages',
        units: 'Logements',
        yearBuilt: 'Année construction',
        asbestosRisk: "Risque d'amiante potentiel",
        asbestosWarning: "Bâtiment construit avant 1990 — prévoir des coûts de désamiantage.",
        associatedAddresses: 'Adresses associées au lot',
        noAddresses: "Aucune adresse civique trouvée dans le rôle d'évaluation",
        vacantLot: 'Le lot peut être vacant ou les données non disponibles'
      },
      financial: {
        scenario: 'Scénario',
        constructionCost: 'Coût de construction',
        marketValue: 'Valeur marchande',
        grossProfit: 'Profit brut',
        roc: 'Rendement (ROC)',
        costPerSqFt: 'Coût/pi²',
        conservative: 'Conservateur',
        standard: 'Standard',
        optimized: 'Optimisé'
      },
      scenarios: {
        vacantLand: 'Terrain vacant',
        existingBuilding: 'Bâtiment existant',
        newConstruction: 'Construction neuve',
        verticalAddition: 'Surélévation',
        demolitionReconstruction: 'Démolition-reconstruction',
        partialRenovation: 'Rénovation partielle',
        buildableFloorArea: 'Surface de plancher',
        estimatedUnits: 'Unités',
        estimatedStoreys: 'Étages',
        footprint: 'Emprise au sol'
      },
      neighborhood: {
        title: 'Contexte du quartier',
        recentPermits: 'Permis récents (500m)',
        permitsIssued: 'Permis émis',
        unitsAuthorized: 'Unités autorisées',
        totalValue: 'Valeur totale',
        marketTrends: 'Tendances du marché'
      },
      sustainability: {
        title: 'Durabilité',
        score: 'Score durabilité',
        certifications: 'Certifications possibles',
        incentives: 'Incitatifs disponibles',
        energySavings: "Économies d'énergie"
      },
      actions: {
        downloadPdf: 'Télécharger PDF',
        delete: 'Supprimer',
        deleteConfirm: 'Supprimer le projet?',
        deleteWarning: 'Cette action est irréversible.',
        runAnalysis: 'Lancer l\'analyse'
      }
    },
    
    // Footer
    footer: {
      copyright: '© 2025 Malvys Exaucé Dessou — ZonePro. Tous droits réservés.'
    },
    
    // Errors
    errors: {
      generic: 'Une erreur est survenue',
      notFound: 'Page non trouvée',
      unauthorized: 'Non autorisé',
      networkError: 'Erreur de connexion'
    },
    
    // Units
    units: {
      sqm: 'm²',
      sqft: 'pi²',
      meters: 'm',
      storeys: 'étages',
      units: 'logements',
      percent: '%'
    }
  },
  
  en: {
    // Common
    common: {
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      cancel: 'Cancel',
      confirm: 'Confirm',
      save: 'Save',
      delete: 'Delete',
      edit: 'Edit',
      back: 'Back',
      next: 'Next',
      search: 'Search',
      close: 'Close',
      yes: 'Yes',
      no: 'No',
      or: 'or',
      and: 'and',
      required: 'Required',
      optional: 'Optional'
    },
    
    // Navigation
    nav: {
      dashboard: 'Dashboard',
      newProject: 'New Project',
      projects: 'Projects',
      admin: 'Administration',
      logout: 'Logout',
      login: 'Login',
      register: 'Sign Up'
    },
    
    // Auth
    auth: {
      login: 'Login',
      register: 'Sign Up',
      email: 'Email',
      password: 'Password',
      fullName: 'Full Name',
      forgotPassword: 'Forgot password?',
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an account?',
      loginButton: 'Sign In',
      registerButton: 'Sign Up',
      loginSuccess: 'Login successful',
      registerSuccess: 'Account created successfully',
      loginError: 'Login error',
      registerError: 'Registration error',
      welcomeBack: 'Welcome back',
      createAccount: 'Create an account',
      startAnalyzing: 'Start analyzing development potential'
    },
    
    // Dashboard
    dashboard: {
      title: 'Dashboard',
      welcome: 'Welcome to ZonePro',
      recentProjects: 'Recent Projects',
      noProjects: 'No projects',
      createFirst: 'Create your first project to get started',
      stats: {
        totalProjects: 'Total Projects',
        analyzed: 'Analyzed',
        draft: 'Drafts',
        avgScore: 'Average Score'
      }
    },
    
    // New Project
    newProject: {
      title: 'New Project',
      subtitle: 'Analyze the development potential of your land',
      lotNumber: 'Cadastral lot number',
      lotPlaceholder: 'Ex: 1 588 300',
      lotHelp: 'Find the number on your deed or at infolot.gouv.qc.ca',
      address: 'Civic address',
      addressPlaceholder: 'Enter an address in Montreal',
      searchLot: 'Analyze this lot',
      searchAddress: 'Search this address',
      searching: 'Searching...',
      analysisLaunched: 'Analysis launched for lot {lot}',
      lotNotFound: 'Lot not found',
      enterLot: 'Please enter a lot number',
      enterAddress: 'Please enter an address',
      orUseAddress: 'Or search by address',
      primaryMethod: 'Primary method',
      alternativeMethod: 'Alternative method',
      projectType: 'Project type',
      residential: 'Residential',
      commercial: 'Commercial',
      mixed: 'Mixed-use',
      dataStatus: {
        confirmed: 'Confirmed',
        computed: 'Computed',
        estimated: 'Estimated',
        unavailable: 'Unavailable',
        planning: 'Planning',
        standard: 'Standard',
        specialSector: 'Special sector',
        requiresValidation: 'Validation required'
      }
    },
    
    // Project Detail
    project: {
      status: {
        analyzed: 'Analyzed',
        draft: 'Draft'
      },
      tabs: {
        overview: 'Overview',
        regulatory: 'Regulatory',
        financial: 'Financial',
        scenarios: 'Scenarios',
        neighborhood: 'Neighborhood',
        sustainability: 'Sustainability'
      },
      score: {
        title: 'Opportunity Score',
        regulatory: 'Regulatory',
        financial: 'Financial',
        neighborhood: 'Neighborhood',
        risk: 'Risk',
        tooltipTotal: 'Evaluates the development potential of the land out of 100 points.',
        tooltipRegulatory: 'Measures compliance with zoning rules.',
        tooltipFinancial: 'Evaluates potential profitability based on costs.',
        tooltipNeighborhood: 'Analyzes urban context and compatibility.',
        tooltipRisk: 'Evaluates potential risks and complexities.'
      },
      overview: {
        recommendedScenario: 'Recommended scenario',
        buildableArea: 'Buildable area',
        estimatedStoreys: 'Estimated storeys',
        estimatedUnits: 'Estimated units',
        riskFactors: 'Risk factors',
        validationRequired: 'Validation required',
        noRisks: 'No major risks identified',
        noValidation: 'No specific validation required'
      },
      regulatory: {
        zoneCode: 'Zone code',
        permittedUses: 'Permitted uses',
        maxHeight: 'Maximum height',
        maxStoreys: 'Maximum storeys',
        lotCoverage: 'Lot coverage',
        setbacks: 'Setbacks',
        front: 'Front',
        rear: 'Rear',
        sides: 'Sides',
        existingBuilding: 'Existing building detected',
        buildingArea: 'Building area',
        floors: 'Floors',
        units: 'Units',
        yearBuilt: 'Year built',
        asbestosRisk: 'Potential asbestos risk',
        asbestosWarning: 'Building constructed before 1990 — plan for asbestos removal costs.',
        associatedAddresses: 'Addresses associated with lot',
        noAddresses: 'No civic addresses found in assessment roll',
        vacantLot: 'Lot may be vacant or data unavailable'
      },
      financial: {
        scenario: 'Scenario',
        constructionCost: 'Construction cost',
        marketValue: 'Market value',
        grossProfit: 'Gross profit',
        roc: 'Return (ROC)',
        costPerSqFt: 'Cost/sq ft',
        conservative: 'Conservative',
        standard: 'Standard',
        optimized: 'Optimized'
      },
      scenarios: {
        vacantLand: 'Vacant land',
        existingBuilding: 'Existing building',
        newConstruction: 'New construction',
        verticalAddition: 'Vertical addition',
        demolitionReconstruction: 'Demolition & reconstruction',
        partialRenovation: 'Partial renovation',
        buildableFloorArea: 'Floor area',
        estimatedUnits: 'Units',
        estimatedStoreys: 'Storeys',
        footprint: 'Footprint'
      },
      neighborhood: {
        title: 'Neighborhood context',
        recentPermits: 'Recent permits (500m)',
        permitsIssued: 'Permits issued',
        unitsAuthorized: 'Units authorized',
        totalValue: 'Total value',
        marketTrends: 'Market trends'
      },
      sustainability: {
        title: 'Sustainability',
        score: 'Sustainability score',
        certifications: 'Possible certifications',
        incentives: 'Available incentives',
        energySavings: 'Energy savings'
      },
      actions: {
        downloadPdf: 'Download PDF',
        delete: 'Delete',
        deleteConfirm: 'Delete project?',
        deleteWarning: 'This action cannot be undone.',
        runAnalysis: 'Run analysis'
      }
    },
    
    // Footer
    footer: {
      copyright: '© 2025 Malvys Exaucé Dessou — ZonePro. All rights reserved.'
    },
    
    // Errors
    errors: {
      generic: 'An error occurred',
      notFound: 'Page not found',
      unauthorized: 'Unauthorized',
      networkError: 'Connection error'
    },
    
    // Units
    units: {
      sqm: 'm²',
      sqft: 'sq ft',
      meters: 'm',
      storeys: 'storeys',
      units: 'units',
      percent: '%'
    }
  }
};

export default LanguageContext;
